/**
 * Shared promotion status taxonomy — the ONE place server step/workflow
 * statuses are mapped to UI visuals. The server emits (promotion.step_status):
 *   pending, applying, awaiting_approval, approved, completed, partial, failed,
 *   retrying, expired, rejected, unknown, lost_in_dr, pending_cache_invalidation
 * and (promotion.workflow_status): in_progress, completed, cancelled,
 * rolled_back, failed.
 *
 * Previous mappers handled 'paused'/'rolled_back'/'sent' — none of which are
 * step statuses — while 'applying' (the primary in-flight state) fell through
 * to 'unknown'/'pending', so an actively-deploying step never pulsed.
 */
import type { PipelineStepStatus } from '../components/ui/PipelineTimeline';

/** Server step status → the timeline/track visual state. */
export function mapStepStatus(raw: string | null | undefined): PipelineStepStatus {
  const s = (raw || '').toLowerCase();
  switch (s) {
    case 'completed':
      return 'completed';
    case 'partial':
      return 'partial';
    case 'failed':
    case 'rejected':
      return 'failed';
    case 'expired':
      return 'expired';
    case 'awaiting_approval':
      return 'awaiting_approval';
    case 'applying':
    case 'retrying':
    case 'approved':
    case 'pending_cache_invalidation':
      return 'in_flight';
    case 'pending':
      return 'pending';
    case 'unknown':
    case 'lost_in_dr':
      return 'unknown';
    default:
      return 'unknown';
  }
}

/** Step statuses a user may (re-)promote from the UI. Mirrors the server's
 *  REPROMOTABLE_STEP_STATUSES (lib/workflowLifecycle.ts). */
export const REPROMOTABLE_STEP_STATUSES = new Set([
  'pending',
  'failed',
  'rejected',
  'expired',
  'unknown',
  'lost_in_dr',
]);

/** Server workflow status → chip key (statusChipColors) + display label. */
export function mapWorkflowStatus(raw: string | null | undefined): {
  chip: string;
  label: string;
} {
  const s = (raw || '').toLowerCase();
  switch (s) {
    case 'in_progress':
      return { chip: 'in_flight', label: 'in flight' };
    case 'completed':
      return { chip: 'completed', label: 'completed' };
    case 'failed':
      return { chip: 'failed', label: 'failed' };
    case 'cancelled':
      return { chip: 'cancelled', label: 'cancelled' };
    case 'rolled_back':
      return { chip: 'rolled_back', label: 'rolled back' };
    default:
      return { chip: 'unknown', label: s || 'unknown' };
  }
}
